import React, { useState, useEffect } from 'react';
import { FaSort, FaTimes, FaGripVertical } from 'react-icons/fa';
import {
    DndContext,
    closestCenter,
    PointerSensor,
    KeyboardSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
    arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import '../css/financeiro-parcela-pagar.css';

/**
 * Equivalente ao `restrictToVerticalAxis` do `@dnd-kit/modifiers`, que não está instalado neste
 * projeto — não vale uma dependência nova para isto. A lib continua dona do estado do arraste e da
 * reordenação; o modifier é só uma função que ela chama para ajustar o transform.
 */
const soEixoVertical = ({ transform }) => ({ ...transform, x: 0 });

/**
 * Modal de reordenação das colunas nomeáveis.
 *
 * O estado do arraste é da própria lib: `SortableContext` + `useSortable` + `arrayMove`, sem
 * cálculo manual de posição. O `soEixoVertical` abaixo prende o movimento ao eixo da lista; o
 * autoscroll do dnd-kit age no ancestral que rola, por isso quem rola aqui é o corpo do modal
 * (maxHeight + overflowY), não a página.
 *
 * O `PointerSensor` tem activation constraint de 6px: sem ela, um clique na linha já iniciava
 * arraste e a lista "pulava" no primeiro toque.
 *
 * A reordenação é rascunho — `Aplicar` só devolve a nova sequência para a tela. Quem grava é o
 * Salvar do evento, no padrão do aéreo (modal aplica no rascunho, nada de endpoint por item).
 */
function LinhaOrdenavel({ coluna, posicao }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: coluna.id });

    return (
        <div
            ref={setNodeRef}
            style={{
                transform: CSS.Transform.toString(transform),
                transition,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '10px 12px',
                marginBottom: 6,
                borderRadius: 8,
                border: '1px solid var(--fin-bd, #e4e7ec)',
                background: isDragging ? 'var(--fin-acw)' : 'var(--fin-sf, #fff)',
                boxShadow: isDragging ? '0 6px 18px rgba(15,23,42,.14)' : 'none',
                cursor: 'grab',
            }}
            {...attributes}
            {...listeners}
        >
            <span className="fin-pp__cell-muted" style={{ display: 'flex', alignItems: 'center' }}>
                <FaGripVertical size={13} />
            </span>
            <span className="fin-pp__cell-muted" style={{ minWidth: 28 }}>{posicao}</span>
            {/* Nome vazio é coluna em edição, não coluna inexistente: o back não grava rótulo em
                branco, então ela tem posição como as outras e precisa aparecer para ser arrastada.
                O rótulo de reserva é `Coluna N`, o mesmo que o back usa quando nasce sem nome. */}
            <span className={(coluna.nome ?? '').trim() !== '' ? 'fin-pp__cell-strong' : 'fin-pp__cell-muted'} style={{ flex: 1, minWidth: 0 }}>
                {(coluna.nome ?? '').trim() !== '' ? coluna.nome : ('Coluna ' + coluna.ordem)}
            </span>
            {!coluna.ativo && <span className="fin-pp__pill is-muted">Travada</span>}
            {coluna.ativo && (coluna.questionar
                ? <span className="fin-pp__pill is-info">Convidado</span>
                : <span className="fin-pp__pill is-muted">Uso interno</span>)}
        </div>
    );
}

export default function ReordenarColunasModal({ show, colunas, onCancelar, onAplicar }) {
    const [rascunho, setRascunho] = useState([]);

    // Reabrir o modal recomeça do estado atual da tela — cancelar não deixa resíduo.
    useEffect(() => {
        if (show) {
            setRascunho(colunas);
        }
    }, [show, colunas]);

    const sensores = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    if (!show) {
        return null;
    }

    const aoSoltar = (evento) => {
        const { active, over } = evento;
        if (!over || active.id === over.id) {
            return;
        }
        const de = rascunho.findIndex(coluna => coluna.id === active.id);
        const para = rascunho.findIndex(coluna => coluna.id === over.id);
        if (de < 0 || para < 0) {
            return;
        }
        setRascunho(arrayMove(rascunho, de, para));
    };

    return (
        <div
            className="modal fade show d-block fin-pp fin-pp__modalbg"
            onClick={(e) => { if (e.target === e.currentTarget) onCancelar(); }}
        >
            <div className="modal-dialog modal-dialog-centered modal-lg">
                <div className="modal-content fin-pp__modal">
                    <div className="fin-pp__modal-head">
                        <span className="fin-pp__modal-head-ic"><FaSort size={17} /></span>
                        <h3 className="fin-pp__modal-title">Reordenar colunas
                            <span className="fin-pp__modal-sub">Arraste para definir a ordem de exibição</span>
                        </h3>
                        <button type="button" className="fin-pp__modal-close" onClick={onCancelar}><FaTimes size={16} /></button>
                    </div>
                    {/* quem rola é este corpo: é o ancestral scrollável que o autoscroll do dnd-kit usa */}
                    <div className="modal-body" style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                        {rascunho.length === 0 ? (
                            <div className="fin-pp__empty">Nenhuma coluna para reordenar.</div>
                        ) : (
                            <DndContext
                                sensors={sensores}
                                collisionDetection={closestCenter}
                                modifiers={[soEixoVertical]}
                                onDragEnd={aoSoltar}
                            >
                                <SortableContext
                                    items={rascunho.map(coluna => coluna.id)}
                                    strategy={verticalListSortingStrategy}
                                >
                                    {rascunho.map((coluna, indice) => (
                                        <LinhaOrdenavel key={coluna.id} coluna={coluna} posicao={indice + 1} />
                                    ))}
                                </SortableContext>
                            </DndContext>
                        )}
                    </div>
                    <div className="fin-pp__modal-foot">
                        <button type="button" className="fin-pp__btn" onClick={onCancelar}>Cancelar</button>
                        <button
                            type="button"
                            className="fin-pp__btn fin-pp__btn--primary"
                            onClick={() => onAplicar(rascunho)}
                        >Aplicar</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
